import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions, WriteRequestOptions } from '../../requestOptions';
import type {
  ExpansionAllowedValuesResponse,
  ExpansionFieldCandidatesResponse,
  ExpansionResolutionCreated,
  ExpansionResolutionPreview,
  ExpansionResolutionRequest,
  ExpansionResolutionsResponse,
} from './types';

/**
 * Resolving a run's gaps with a subscription commonality rule — "set a value for every product" or
 * "use a product field" — from the Next steps panel. Spec:
 * docs/superpowers/specs/2026-10-01-expansion-resolve-gaps-design.md (API section). Every route is
 * org-scoped like the other run endpoints: the subscription header authorises the caller, the run
 * id is scoped by organization.
 */

type RunParams = {
  runId: number;
  /** Any subscription of the organization — see `getRunFamilies`. */
  subscriptionId: number;
};

const validateRun = (runId: number, subscriptionId: number) => {
  if (!runId || runId < 1) {
    throw new Error('Valid run ID is required');
  }
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
};

const validateRequest = (request: ExpansionResolutionRequest) => {
  if (!request.key) {
    throw new Error('A commonality key is required');
  }
  if (request.kind === 'value' && request.value.trim() === '') {
    throw new Error('Pick a value');
  }
  if (request.kind === 'field' && request.field.trim() === '') {
    throw new Error('Pick a product field');
  }
};

const headers = (subscriptionId: number) => ({ 'x-astur-subscription-id': subscriptionId.toString() });

/**
 * The body exactly as the API names it: `value` for a value, `field` for a field — never both, and
 * never a stray empty one. Built field by field rather than spread, so nothing a caller's object
 * happens to carry reaches the API.
 */
export const resolutionBody = (request: ExpansionResolutionRequest, createdBy?: string): Record<string, string> => {
  const body: Record<string, string> = { key: request.key, kind: request.kind, mode: request.mode };
  if (request.kind === 'value') body.value = request.value;
  else body.field = request.field;
  if (createdBy) body.created_by = createdBy;
  return body;
};

/** A dry run: what the rule would fill, unlock, refuse and replace. Nothing is saved. */
export const previewResolution = async (
  scope: RequestScope,
  { runId, subscriptionId, request }: RunParams & { request: ExpansionResolutionRequest },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<ExpansionResolutionPreview>> => {
  try {
    validateRun(runId, subscriptionId);
    validateRequest(request);

    const result = await scopedChannelApi(scope).post<ExpansionResolutionPreview>(
      `atts/expansion/runs/${runId}/resolutions/preview`,
      resolutionBody(request),
      // A dry run: a POST that saves nothing, so it is a read to the session layer — it may carry
      // the workflow's abort signal and is dropped, not finished, when the scope changes.
      { ...options, headers: headers(subscriptionId), _sessionSemanticRead: true }
    );

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

/**
 * Creates the rule on the destination subscription and re-computes this run's rows for the key.
 * 409 when a subscription rule already exists for the key — this version never overwrites one.
 */
export const createResolution = async (
  scope: RequestScope,
  { runId, subscriptionId, request, createdBy }: RunParams & { request: ExpansionResolutionRequest; createdBy?: string },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<ExpansionResolutionCreated>> => {
  try {
    validateRun(runId, subscriptionId);
    validateRequest(request);

    const result = await scopedChannelApi(scope).post<ExpansionResolutionCreated>(
      `atts/expansion/runs/${runId}/resolutions`,
      resolutionBody(request, createdBy),
      { ...options, headers: headers(subscriptionId) }
    );

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

/**
 * Undoes a resolution: deletes the rule it created and puts the run's rows back to gaps. 409 when
 * the rule was changed since (edited in Commonality) — the API refuses rather than delete a
 * human's edit.
 */
export const undoResolution = async (
  scope: RequestScope,
  { runId, subscriptionId, resolutionId, undoneBy }: RunParams & { resolutionId: number; undoneBy?: string },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<unknown>> => {
  try {
    validateRun(runId, subscriptionId);
    if (!resolutionId || resolutionId < 1) {
      throw new Error('Valid resolution ID is required');
    }

    const result = await scopedChannelApi(scope).delete<unknown>(`atts/expansion/runs/${runId}/resolutions/${resolutionId}`, {
      ...options,
      headers: headers(subscriptionId),
      // Who undid it, recorded the way `created_by` is. Sent only when known.
      ...(undoneBy ? { data: { undone_by: undoneBy } } : {}),
    });

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

/** The rules created from this run, newest first, undone ones included. */
export const listResolutions = async (
  scope: RequestScope,
  { runId, subscriptionId }: RunParams,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<ExpansionResolutionsResponse>> => {
  try {
    validateRun(runId, subscriptionId);

    const result = await scopedChannelApi(scope).get<ExpansionResolutionsResponse>(`atts/expansion/runs/${runId}/resolutions`, {
      ...options,
      headers: headers(subscriptionId),
    });

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

/** Candidate product fields for a key, with their fill rate and sample values among the key's gap products. */
export const getRunFields = async (
  scope: RequestScope,
  { runId, subscriptionId, key }: RunParams & { key: string },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<ExpansionFieldCandidatesResponse>> => {
  try {
    validateRun(runId, subscriptionId);
    if (!key) {
      throw new Error('A commonality key is required');
    }

    const result = await scopedChannelApi(scope).get<ExpansionFieldCandidatesResponse>(`atts/expansion/runs/${runId}/fields`, {
      ...options,
      headers: headers(subscriptionId),
      params: { key },
    });

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

/**
 * The key's allowed values on the destination, for the Value tab's dropdown. `q` (a case-insensitive
 * substring of either field) narrows a list the API truncated; sent only when non-empty.
 */
export const getRunAllowedValues = async (
  scope: RequestScope,
  { runId, subscriptionId, key, q }: RunParams & { key: string; q?: string },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<ExpansionAllowedValuesResponse>> => {
  try {
    validateRun(runId, subscriptionId);
    if (!key) {
      throw new Error('A commonality key is required');
    }

    const result = await scopedChannelApi(scope).get<ExpansionAllowedValuesResponse>(`atts/expansion/runs/${runId}/allowed-values`, {
      ...options,
      headers: headers(subscriptionId),
      params: { key, q: q?.trim() ? q.trim() : undefined },
    });

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
